/**
 * AudioRangeSource — PCM любого места записи, не раскодируя запись.
 *
 * Зачем. Вопрос «что в этой записи» (речь или музыка, какой у неё шум, где в
 * ней тишина) задаётся записи ЦЕЛИКОМ, а раскодировать целиком длинную запись
 * нельзя: 70 минут — это 1,5 ГБ PCM. До сих пор каждый, кому нужен был ответ,
 * брал НАЧАЛО: /denoise судил запись по первым 12 секундам (чат Премиума 685,
 * 14.10.2026: мюзикл на 71 минуту начался с объявления — «речь», профиль
 * «Голос», срез низа на 120 Гц; за 30 дней так прочитано 549 записей), экспорт
 * «Ручной настройки» /audio-enhancer без буфера не работал вовсе.
 *
 * Как. Файл монтируется в FFmpeg один раз (WORKERFS, в память не копируется),
 * дальше любое место читается отдельным коротким запуском: `-ss` до `-i`
 * (перемотка по контейнеру), лид-ин в секунду на целых секундах, обрезка по
 * отсчётам. Стыки соседних блоков, начатых на целых секундах, посэмплово равны
 * непрерывному декоду (замер /speech-restorer, 25.09.2026).
 *
 *   var src = await AudioRangeSource.open(file, { FFmpegProcessor: FFmpegProcessor });
 *   src.info                      → { duration, sampleRate, channels, codec }
 *   await src.read(startFrame, frames, sampleRate)   → Float32Array[] (по каналу)
 *   await src.close()
 *
 *   // Показательная выборка записи: `spans` кусков по `spanSec`, равномерно
 *   // по длине (25 % / 50 % / 75 % при трёх), сшитых в один буфер.
 *   var ex = await AudioRangeSource.spread(file, { FFmpegProcessor, durSec, ctx });
 *   ex.buffer                     → AudioBuffer
 *   ex.spans                      → [{ at, start, dur }]  (at — секунда записи, start — секунда буфера)
 *   ex.toFileTime(t)              → секунда записи для секунды буфера
 *
 * Разбор — docs/why/primitives/long-audio-chain.md («Вход — AudioRangeSource»).
 */
(function (root) {
    'use strict';

    var DEC_PRE_S = 1;
    var DEC_POST_S = 0.5;
    var MAX_CHANNELS = 8;

    /** Проба FFmpeg (`-i файл` без выхода) → длина, частота, каналы, кодек. */
    function parseProbe(logs) {
        logs = String(logs || '');
        var m = logs.match(/Duration:\s*(\d+):(\d+):(\d+(?:\.\d+)?)/);
        var dur = m ? (+m[1] * 3600 + +m[2] * 60 + +m[3]) : 0;
        var a = logs.match(/Audio:\s*([^,\n]+),\s*(\d+)\s*Hz,\s*([^,\n]+)/);
        return {
            duration: dur,
            sampleRate: a ? +a[2] : 0,
            channels: a ? layoutChannels(a[3].trim()) : 0,
            codec: a ? a[1].trim() : ''
        };
    }

    /** Раскладку с именем («5.1(side)», «quad») FFmpeg сводит к числу сам через `-ac`. */
    function layoutChannels(layout) {
        if (/^mono\b/.test(layout)) return 1;
        if (/^stereo\b|^2 channels/.test(layout)) return 2;
        var m = layout.match(/^(\d+) channels/);
        if (m) return Math.max(1, Math.min(MAX_CHANNELS, +m[1]));
        if (/^7\.1/.test(layout)) return 8;
        if (/^6\.1/.test(layout)) return 7;
        if (/^5\.1/.test(layout)) return 6;
        if (/^5\.0|^4\.1/.test(layout)) return 5;
        if (/^quad|^4\.0|^3\.1/.test(layout)) return 4;
        if (/^2\.1|^3\.0/.test(layout)) return 3;
        return layout ? 2 : 0;
    }

    /**
     * Сведение в моно — СРЕДНЕЕ каналов, как у Web Audio (0,5·(L+R)): файл и
     * раскодированный буфер обязаны давать один сигнал. Голый `-ac 1` FFmpeg
     * сводит стерео как 0,707·(L+R) — на 3 дБ громче (замер 14.10.2026 на
     * песне из репозитория: отношение RMS ровно √2). Многоканальное — по
     * раскладке, тем же матричным сведением, что `-ac 1` (там они совпадают
     * бит-в-бит).
     */
    function monoDownmix(srcCh) {
        if (srcCh === 2) return 'pan=mono|c0=0.5*c0+0.5*c1,';
        if (srcCh > 2) return 'aformat=channel_layouts=mono,';
        return '';
    }

    function decodeArgs(path, sr, ch, startFrame, n, out, srcCh) {
        var pre = Math.min(DEC_PRE_S, Math.floor(startFrame / sr));
        var t0 = startFrame / sr - pre;
        var preS = pre * sr;
        var args = ['-hide_banner', '-nostdin'];
        if (t0 > 0) args.push('-ss', t0.toFixed(6));
        args.push('-t', (n / sr + pre + DEC_POST_S).toFixed(6), '-i', path, '-vn',
            '-af', (ch === 1 ? monoDownmix(srcCh | 0) : '') + 'aresample=' + sr + ',atrim=start_sample=' + preS + ':end_sample=' + (preS + n),
            '-f', 'f32le', '-ac', String(ch), '-y', out);
        return args;
    }

    function deinterleave(bytes, ch) {
        var usable = bytes.byteLength - (bytes.byteLength % (4 * ch));
        var ab = (bytes.byteOffset % 4 === 0)
            ? bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + usable)
            : bytes.slice(0, usable).buffer;
        var f = new Float32Array(ab);
        if (ch === 1) return [f];
        var n = f.length / ch, out = [], c, i;
        for (c = 0; c < ch; c++) out.push(new Float32Array(n));
        for (i = 0; i < n; i++) for (c = 0; c < ch; c++) out[c][i] = f[i * ch + c];
        return out;
    }

    function fail(code, message, logs) {
        var e = new Error(message);
        e.code = code;
        if (logs) e.logs = String(logs).slice(-400);
        return e;
    }

    var _seq = 0;

    /**
     * Открыть запись. deps.FFmpegProcessor — настоящий (не заглушка ядра),
     * уже инициализированный либо инициализируемый `mountInput`.
     */
    async function open(file, deps) {
        var FP = deps && deps.FFmpegProcessor;
        if (!FP || !FP._fs || typeof FP.mountInput !== 'function') throw fail('ARS_NO_ENGINE', 'FFmpeg engine is not available');
        var fs = FP._fs;
        var mount = await FP.mountInput(file);
        var info;
        try {
            var probe = await fs.exec(['-hide_banner', '-i', mount.path]);
            info = parseProbe(probe && probe.logs);
        } catch (e) {
            try { await mount.dispose(); } catch (_) {}
            throw e;
        }
        if (!info.channels) {
            try { await mount.dispose(); } catch (_) {}
            throw fail('ARS_NO_AUDIO', 'no audio stream in the file');
        }
        var tag = 'ars' + (_seq++);
        var n = 0, closed = false;
        return {
            info: info,
            /** Отсчёты [startFrame, startFrame + frames) на частоте `sampleRate`
             *  (по умолчанию — родной), по массиву на канал. Короче просимого —
             *  запись кончилась. `channels` — свести к этому числу каналов
             *  (в моно — средним, см. monoDownmix). */
            read: async function (startFrame, frames, sampleRate, channels) {
                if (closed) throw fail('ARS_CLOSED', 'source is closed');
                var sr = sampleRate > 0 ? Math.round(sampleRate) : info.sampleRate;
                var ch = channels > 0 ? Math.min(MAX_CHANNELS, channels | 0) : info.channels;
                var out = '_' + tag + '_' + (n++ % 2) + '.pcm';
                var r = await fs.exec(decodeArgs(mount.path, sr, ch, Math.max(0, startFrame | 0), frames | 0, out, info.channels));
                if (!r || r.code !== 0) {
                    try { await fs.del(out); } catch (_) {}
                    throw fail('ARS_DECODE', 'decode failed at ' + (startFrame / sr).toFixed(1) + ' s', r && r.logs);
                }
                var bytes;
                try { bytes = await fs.read(out); } catch (_) { bytes = new Uint8Array(0); }
                try { await fs.del(out); } catch (_) {}
                return deinterleave(bytes, ch);
            },
            close: function () {
                if (closed) return Promise.resolve();
                closed = true;
                return Promise.resolve(mount.dispose ? mount.dispose() : null);
            }
        };
    }

    /** Где брать куски: центры на (i + 1) / (spans + 1) длины — у трёх это
     *  25 %, 50 % и 75 %. Начало и конец записи не берутся намеренно: там
     *  объявление, аплодисменты, заставка, тишина перед первой фразой. Запись
     *  короче суммы кусков отдаётся одним куском целиком. */
    function spreadPlan(durSec, spans, spanSec) {
        var k = Math.max(1, spans | 0), d = Math.max(1, +spanSec || 12);
        if (!(durSec > 0)) return [{ at: 0, dur: d }];
        if (durSec <= k * d) return [{ at: 0, dur: durSec }];
        var out = [];
        for (var i = 0; i < k; i++) {
            var centre = durSec * (i + 1) / (k + 1);
            // на целой секунде: перемотка и обрезка совпадают с проверенной сеткой
            out.push({ at: Math.max(0, Math.min(Math.floor(durSec - d), Math.round(centre - d / 2))), dur: d });
        }
        return out;
    }

    /**
     * Показательная выборка записи одним буфером.
     *
     * o.FFmpegProcessor, o.durSec (если известна — иначе из пробы),
     * o.spans (3), o.spanSec (12), o.sampleRate (частота буфера; по умолчанию
     * o.ctx.sampleRate или родная), o.nativeRate (родная частота записи, не выше
     * o.maxRate = 48000), o.maxChannels (2), o.ctx (для createBuffer).
     */
    async function spread(file, o) {
        o = o || {};
        var src = await open(file, { FFmpegProcessor: o.FFmpegProcessor });
        try {
            var dur = src.info.duration > 0 ? src.info.duration : (o.durSec > 0 ? o.durSec : 0);
            var sr = o.sampleRate > 0 ? Math.round(o.sampleRate) : ((o.ctx && o.ctx.sampleRate) || src.info.sampleRate || 48000);
            // The recording's own rate (capped): for a caller that will PROCESS at the
            // rate of the excerpt — a 16 kHz call cleaned at 48 kHz is three times the file.
            if (o.nativeRate && src.info.sampleRate > 0) sr = Math.min(src.info.sampleRate, o.maxRate > 0 ? o.maxRate : 48000);
            var ch = Math.max(1, Math.min(o.maxChannels > 0 ? o.maxChannels : 2, src.info.channels));
            var plan = spreadPlan(dur, o.spans || 3, o.spanSec || 12);
            var pieces = [], spans = [], total = 0;
            for (var i = 0; i < plan.length; i++) {
                var chans = await src.read(Math.round(plan[i].at * sr), Math.round(plan[i].dur * sr), sr, ch);
                if (!chans[0].length) continue;
                spans.push({ at: plan[i].at, start: total / sr, dur: chans[0].length / sr });
                pieces.push(chans);
                total += chans[0].length;
            }
            if (!total) throw fail('ARS_EMPTY', 'nothing could be decoded from the recording');
            var buffer = makeBuffer(o.ctx, ch, total, sr);
            var off = 0;
            for (var p = 0; p < pieces.length; p++) {
                for (var c = 0; c < ch; c++) buffer.copyToChannel(pieces[p][c], c, off);
                off += pieces[p][0].length;
            }
            return {
                buffer: buffer, spans: spans, duration: dur, info: src.info,
                toFileTime: function (t) { return toFileTime(spans, t); }
            };
        } finally {
            try { await src.close(); } catch (_) {}
        }
    }

    function makeBuffer(ctx, ch, length, sr) {
        if (ctx && typeof ctx.createBuffer === 'function' && ctx.sampleRate === sr) return ctx.createBuffer(ch, length, sr);
        if (typeof root.AudioBuffer === 'function') return new root.AudioBuffer({ numberOfChannels: ch, length: length, sampleRate: sr });
        var OAC = root.OfflineAudioContext || root.webkitOfflineAudioContext;
        return new OAC(ch, 1, sr).createBuffer(ch, length, sr);
    }

    /** Секунда сшитого буфера → секунда записи (кусок, в который она попала). */
    function toFileTime(spans, t) {
        for (var i = spans.length - 1; i >= 0; i--) {
            if (t >= spans[i].start) return spans[i].at + Math.min(t - spans[i].start, spans[i].dur);
        }
        return spans.length ? spans[0].at : 0;
    }

    root.AudioRangeSource = {
        open: open,
        spread: spread,
        spreadPlan: spreadPlan,
        toFileTime: toFileTime,
        parseProbe: parseProbe,
        layoutChannels: layoutChannels,
        decodeArgs: decodeArgs,
        monoDownmix: monoDownmix,
        deinterleave: deinterleave
    };
})(typeof window !== 'undefined' ? window : globalThis);
